import { ElementRef, useEffect, useMemo, useRef, useState } from 'react';

import rfdc from 'rfdc';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ResizeObserver from 'resize-observer-polyfill';

import { ShowMoreApms } from '../PaymentMethod/ShowMoreApm';
import { useCheckout } from '../../../../hooks/use-checkout';
import { useAPMsToExclude } from './hooks/use-APMs-to-exclude';
import { usePaymentState } from '../../hooks/use-payment-state';
import { PAYMENT_METHODS_NAMES_TO_DISPLAY_NAMES } from './utils';
import { sendMixpanelEvent } from '../../../../utils/mixpanel-utils';
import { useDeviceHelper } from '../../../../hooks/use-device-helper';
import { HiddenPaymentMethod } from '../PaymentMethod/HiddenPaymentMethod';
import { VisiblePaymentMethod } from '../PaymentMethod/VisiblePaymentMethod';
import { EBEPaymentMethodsName, MixpanelEvents, PaymentMethod } from '../../../../contracts';
import { logInfo } from '../../../../utils/coralogix-rum';

import './style.scss';

interface PaymentsMethodsProps {
  isHiddenApmsShown: boolean;
  setIsHiddenApmsShown: (value: boolean) => void;
}

const clone = rfdc();

export function Paymentmethods({ isHiddenApmsShown, setIsHiddenApmsShown }: PaymentsMethodsProps) {
  const APMsToExclude = useAPMsToExclude();
  const [numOfVisibleItems, setNumOfVisibleItems] = useState(4);
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const { checkoutData, onPaymentMethodChange, selectedPaymentMethod, getMixpanelEventParams } =
    useCheckout();
  const paymentMethods = checkoutData?.paymentMethods;
  const { isMobile } = useDeviceHelper();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const containerRef = useRef<ElementRef<'div'>>();
  const visibleItems = useMemo(() => {
    const filteredPMS = paymentMethods?.filter(
      (pm) => !APMsToExclude.includes(pm.paymentMethodName as EBEPaymentMethodsName),
    );

    const selectedPMIndex = filteredPMS?.findIndex(
      (pm) => pm.paymentMethodName === selectedPaymentMethod?.paymentMethodName,
    );
    const isVisible = selectedPMIndex! < numOfVisibleItems;
    if (isVisible) {
      return filteredPMS;
    }
    const newPaymentMethods = clone(filteredPMS);
    const removedPM = newPaymentMethods?.splice(selectedPMIndex!, 1);
    newPaymentMethods?.splice(numOfVisibleItems - 1, 0, removedPM?.[0]!);
    return newPaymentMethods;
  }, [numOfVisibleItems, paymentMethods, selectedPaymentMethod, APMsToExclude]);

  useEffect(() => {
    const myObserver = new ResizeObserver((entries) => {
      const containerWidth = entries[0].contentRect.width;
      const numOfItems = Math.floor((containerWidth - 30) / 78);
      setNumOfVisibleItems(numOfItems);
    });
    const ref = containerRef?.current as Element;
    myObserver.observe(ref);

    return () => myObserver.unobserve(ref);
  }, []);

  const onPaymentMethodSelect = (pm: PaymentMethod) => {
    sendMixpanelEvent(MixpanelEvents.PAYMENT_CHOOSE, {
      ...getMixpanelEventParams(),
      paymentMethodName: pm.paymentMethodName,
    });
    setIsHiddenApmsShown(false);
    onPaymentMethodChange(pm);
  };

  const hiddenAPMS = visibleItems?.slice(numOfVisibleItems);
  const showMoreAMPS = Boolean(hiddenAPMS?.length);

  useEffect(() => {
    if (visibleItems && visibleItems.length > 0) {
      try {
        const visiblePMs = visibleItems.slice(0, numOfVisibleItems);
        const hiddenPMs = visibleItems.slice(numOfVisibleItems);

        logInfo('Payment methods displayed', {
          totalAvailablePaymentMethods: visibleItems.length,
          numberOfVisiblePaymentMethods: visiblePMs.length,
          numberOfHiddenPaymentMethods: hiddenPMs.length,
          visiblePaymentMethods: visiblePMs.map((pm) => ({
            id: pm.id,
            name: pm.paymentMethodName,
            displayName: pm.paymentMethodDisplayName,
            provider: pm.provider,
          })),
          hiddenPaymentMethods: hiddenPMs.map((pm) => ({
            id: pm.id,
            name: pm.paymentMethodName,
            displayName: pm.paymentMethodDisplayName,
            provider: pm.provider,
          })),
          selectedPaymentMethod: {
            id: selectedPaymentMethod?.id,
            name: selectedPaymentMethod?.paymentMethodName,
            displayName: selectedPaymentMethod?.paymentMethodDisplayName,
            provider: selectedPaymentMethod?.provider,
          },
          excludedPaymentMethods: APMsToExclude,
          isMobile,
        });
      } catch (error) {
        console.error('Error logging displayed payment methods:', error);
      }
    }
  }, [visibleItems, numOfVisibleItems, selectedPaymentMethod, APMsToExclude, isMobile]);

  return (
    <Box className="payment-methods" style={{ pointerEvents: isPaymentLoading ? 'none' : 'auto' }}>
      <Box
        className="visible-apms"
        ref={containerRef}
        mb={isMobile ? 1.5 : 3}
        mt={isMobile ? 1.5 : 3}
      >
        {visibleItems?.slice(0, numOfVisibleItems).map((pm) => {
          const isSelected = selectedPaymentMethod?.paymentMethodName === pm.paymentMethodName;
          const isCreditCard = pm?.paymentMethodName === EBEPaymentMethodsName.card;
          const displayName =
            PAYMENT_METHODS_NAMES_TO_DISPLAY_NAMES[pm.paymentMethodName] ||
            pm.paymentMethodDisplayName;
          return (
            <VisiblePaymentMethod
              paymentMethodData={pm}
              displayName={isCreditCard ? t('creditCard') : displayName}
              isSelected={!isHiddenApmsShown && isSelected}
              onPaymentMethodChange={onPaymentMethodSelect}
              key={pm.id}
            />
          );
        })}
        {showMoreAMPS && (
          <ShowMoreApms
            isSelected={isHiddenApmsShown}
            showHiddenApmsList={() => setIsHiddenApmsShown(true)}
          />
        )}
      </Box>
      {isHiddenApmsShown && (
        <Box className="hidden-apms">
          {hiddenAPMS?.map((pm) => {
            const isCreditCard = pm?.paymentMethodName === EBEPaymentMethodsName.card;
            const displayName =
              PAYMENT_METHODS_NAMES_TO_DISPLAY_NAMES[pm.paymentMethodName] ||
              pm.paymentMethodDisplayName;
            return (
              <HiddenPaymentMethod
                paymentMethodData={pm}
                displayName={isCreditCard ? t('creditCard') : displayName}
                onPaymentMethodChange={onPaymentMethodSelect}
                key={pm.id}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
